import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { BORDER_COLORS, FONT_COLORS } from "../../constants";
import ProductCard from "./partials/ProductCard";
import ProductInfo from "./partials/ProductInfo";
import ProductMetrics from "./partials/ProductMetrics";
import { CircularProgress, SearchInput } from "../../components";
import { useEffect, useRef } from "react";
import { searchByQuery } from "../../features/search/search.thunk";
import { loadMore, resetSearchState } from "../../features/search/search.slice";
import { ReactComponent as EmptyIcon } from "../../assets/empty-icon.svg";
import CenteredContent from "./partials/CenteredContent";
import { formatNumber } from "../../helpers";
import { useSearch } from "../../hooks/useSearch";
import { selectExtension } from "../../features/extension/extension.selector";

const Search = () => {
  const [searchParam] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { products, displayCount, error, isLoading, lastSearchQuery } = useAppSelector(
    (state) => state.search,
  );
  const { isExtension } = useAppSelector(selectExtension);

  const query = searchParam.get("q");

  const getSearchRoute = (q: string) => {
    return isExtension ? `/product-analyzer/search?q=${q}` : `/search?q=${q}`;
  };

  const { searchValue, setSearchValue, handleSearch } = useSearch({
    onSuccess: ({ status, q }) => {
      if (status === "multiple_products") {
        navigate(getSearchRoute(q));
      } else if (status === "single_product") {
        navigate(`/product-analyzer?asin=${q}`);
      }
    },
  });

  const ref = useRef<HTMLInputElement>(null);
  const lastFetchedQuery = useRef<string | null>(null);

  const visibleProducts = products.slice(0, displayCount);
  const hasMore = displayCount < products.length;

  useEffect(() => {
    if (!query) {
      navigate("/product-analyzer", { replace: true });
      return;
    }

    // Products already fetched for this query (e.g., navigated here from a header search)
    if (lastSearchQuery === query && products.length > 0) {
      lastFetchedQuery.current = query;
      return;
    }

    // Query changed from a previous search on this page — reset stale results
    if (lastFetchedQuery.current !== null && query !== lastFetchedQuery.current) {
      lastFetchedQuery.current = query;
      dispatch(resetSearchState());
      return;
    }

    lastFetchedQuery.current = query;

    if (!products.length && !error) {
      dispatch(searchByQuery({ q: query }))
        .unwrap()
        .then((res) => {
          if (!res.length) {
            navigate(`/product-analyzer?q=${query}&noProducts=true`);
          }
        })
        .catch(() => {});
    }
  }, [dispatch, navigate, products, error, query, lastSearchQuery]);

  if (isLoading && !products.length) {
    return (
      <CenteredContent>
        <CircularProgress size="lg" />
      </CenteredContent>
    );
  }

  if (error) {
    return (
      <CenteredContent>
        <EmptyIcon />
        <Typography variant="caption" mb={1}>
          No results for "{query}"
        </Typography>
        <Button
          onClick={() => navigate("/product-analyzer", { replace: true })}
        >
          Go Back
        </Button>
      </CenteredContent>
    );
  }

  return (
    <Box>
      {isExtension && (
        <Stack direction="row" gap="8px" maxWidth="480px" marginBottom="16px">
          <SearchInput
            ref={ref}
            autoFocus
            value={searchValue}
            disabled={isLoading}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder=""
            onKeyDown={(e) => {
              if (e.code === "Enter") {
                handleSearch(searchValue);
              }
            }}
          />
          <Button
            variant="contained"
            size="large"
            disabled={!searchValue.length}
            loading={isLoading}
            onClick={() => handleSearch(searchValue)}
          >
            Search
          </Button>
        </Stack>
      )}

      <Typography variant="body1" marginBottom="4px">
        {formatNumber(products.length, { useAbbreviation: false })} results for{" "}
        <span style={{ color: FONT_COLORS.info, fontWeight: "bold" }}>
          "{query}"
        </span>
      </Typography>

      <Box>
        {visibleProducts.map((product) => {
          const imageSrc = product.images?.[0];

          return (
            <ProductCard key={product.asin}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={0.5}
                width={120}
                minWidth={120}
                height={120}
                borderRight={`1px solid ${BORDER_COLORS.default}`}
              >
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={product.title}
                    width="120px"
                    height="120px"
                    style={{ objectFit: "contain" }}
                  />
                ) : (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={0.5}
                    sx={{ color: FONT_COLORS.subheading }}
                  >
                    <HideImageOutlinedIcon sx={{ fontSize: 32 }} />
                    <Typography variant="caption" color="inherit">
                      No image
                    </Typography>
                  </Stack>
                )}
              </Box>
              <Box p={1} flexGrow={1}>
                <ProductInfo
                  product={product}
                  onProductClick={(asin) =>
                    navigate(`/product-analyzer?asin=${asin}`)
                  }
                />
                <ProductMetrics product={product} />
              </Box>
            </ProductCard>
          );
        })}
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        mt="8px"
        flexDirection="row-reverse"
      >
        <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
          Go back
        </Button>

        {hasMore && (
          <Button
            variant="contained"
            size="large"
            onClick={() => dispatch(loadMore())}
          >
            Load More
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Search;
